import { inject, Injectable, signal } from '@angular/core';
import { AccountInfo } from '@groundworks/ngx-groundworks-auth';
import { EmptyError, firstValueFrom } from 'rxjs';
import { BranchService } from './branch.service';
import { DefaultBranchService } from './default-branch.service';
import { ProfileResponse, ProfileService } from './profile.service';
import { isInteractionRequiredError, retryTransient } from './retry-transient';

/**
 * Set before the automatic sign-in redirect and cleared once a Graph token is acquired (or the
 * redirect fails to start), so an interaction-required error that survives the redirect shows
 * the failure instead of redirecting in a loop. A canceled redirect leaves it set, so the user
 * sees the failure instead of being sent back to the sign-in page.
 */
export const REAUTH_ATTEMPTED_KEY = 'sales.post-login.reauth-attempted';

/** The post-login step that failed and left the user without their name or roles. */
export type PostLoginFailure = 'profile' | 'roles';

// Runs once the active account is set (in practice once per page load) and is never retried by
// the caller. Each Graph and API call therefore retries transient failures itself. An expired
// session redirects to sign in again, at most once per tab session. Any other outcome that
// leaves the profile or roles unloaded (a canceled or repeated redirect included) is logged,
// published through `failure` for the shell to show, and not retried until the next page load.
// Roles and branch selection each catch their own failure, so both are logged (Promise.all
// would surface only the first), and run() settles only after both finish.
@Injectable({ providedIn: 'root' })
export class PostLoginService {
  private readonly profileService = inject(ProfileService);
  private readonly branchService = inject(BranchService);
  private readonly defaultBranchService = inject(DefaultBranchService);

  readonly failure = signal<PostLoginFailure | null>(null);

  async run(account: AccountInfo): Promise<void> {
    this.failure.set(null);
    let accessToken: string;
    let profile: ProfileResponse;
    try {
      // Token and profile retry together, so a failed profile load retries with a fresh token.
      ({ accessToken, profile } = await retryTransient(async () => {
        const token = await this.profileService.getAccountAccessToken();
        // The session works again, so a later expiry in this tab may redirect again.
        clearReauthAttempt();
        return { accessToken: token, profile: await this.profileService.getProfile(token) };
      }));
    } catch (error) {
      console.error('Failed to load the Graph profile:', error);
      if (isInteractionRequiredError(error)) {
        await this.signInAgainOnce();
      }
      // Reached only if no redirect navigated away.
      this.failure.set('profile');
      return;
    }

    const hasIdTokenClaims = !!account.idTokenClaims;
    if (hasIdTokenClaims) {
      this.profileService.setProfile(profile);
    }
    await Promise.all([
      hasIdTokenClaims ? this.applyRoles(accessToken) : Promise.resolve(),
      this.setDefaultBranch(profile.officeLocation),
    ]);
  }

  /**
   * Starts the sign-in redirect unless one was already attempted this session. A redirect that
   * navigates away never settles, so this resolves only when the page is still alive: already
   * attempted, storage unavailable, the redirect failed to start, or the user came back from the
   * sign-in page (canceled; the latch stays set).
   */
  private async signInAgainOnce(): Promise<void> {
    if (!claimReauthAttempt()) {
      return;
    }
    try {
      await this.profileService.signInAgain();
      console.warn('The sign-in redirect was canceled');
    } catch (error) {
      console.error('Failed to start the sign-in redirect:', error);
      // No redirect happened, so the next page load may try again.
      clearReauthAttempt();
    }
  }

  private async applyRoles(accessToken: string): Promise<void> {
    try {
      await retryTransient(() => this.profileService.applyRoles(accessToken));
    } catch (error) {
      console.error('Failed to load the user roles:', error);
      this.failure.set('roles');
    }
  }

  private async setDefaultBranch(locationName: string | null): Promise<void> {
    if (!locationName) {
      console.warn('No default branch set: the Graph profile has no office location');
      return;
    }
    try {
      const branches = await retryTransient(() => firstValueFrom(this.branchService.getBranches()));

      const matches = branches.filter((b) => b.name === locationName);
      if (matches.length === 1) {
        this.branchService.setSelectedBranches([matches[0]]);
        this.defaultBranchService.notifyDefaultBranchSet();
      } else {
        console.warn(`No default branch set: ${matches.length} branches match office location "${locationName}"`);
      }
    } catch (error) {
      if (error instanceof EmptyError) {
        // The MSAL interceptor completes a request without a value whenever it cannot get the API
        // token silently, for any reason (a network failure included). It then starts a sign-in
        // redirect without awaiting it, so the page usually, but not always, navigates away. It
        // logs neither the silent failure nor a redirect that fails to start.
        console.warn(
          'No default branch set: the branch request completed without a response. The MSAL ' +
            'interceptor does this when it cannot get the API token silently; it then attempts a ' +
            'sign-in redirect.'
        );
        return;
      }
      console.error('Failed to set the default branch from the office location:', error);
    }
  }
}

/** Records a sign-in redirect attempt; false if one was already made or storage is unavailable. */
function claimReauthAttempt(): boolean {
  try {
    if (sessionStorage.getItem(REAUTH_ATTEMPTED_KEY) !== null) {
      return false;
    }
    sessionStorage.setItem(REAUTH_ATTEMPTED_KEY, 'true');
    return true;
  } catch {
    return false;
  }
}

function clearReauthAttempt(): void {
  try {
    sessionStorage.removeItem(REAUTH_ATTEMPTED_KEY);
  } catch {
    // Storage is unavailable, so nothing was recorded.
  }
}
